import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, TrendingUp, TrendingDown, FileText, Factory,
  ChevronDown, ChevronRight, LogOut, Wallet, Menu, X, Settings, Shield, Clock
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function usePendingCount(enabled) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem("khn_token");
        if (!token) return;
        const r = await axios.get(`${API}/api/users/pending/count`, { headers: { Authorization: `Bearer ${token}` } });
        if (live) setCount(r.data?.pending_approval || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => { live = false; clearInterval(id); };
  }, [enabled]);
  return count;
}

let _bizTypesCache = null;
function useBusinessTypes() {
  const [types, setTypes] = useState(_bizTypesCache || []);
  useEffect(() => {
    if (_bizTypesCache) return;
    const token = localStorage.getItem("khn_token");
    if (!token) return;
    axios.get(`${API}/api/business-types`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { _bizTypesCache = r.data; setTypes(r.data); })
      .catch(() => {});
  }, []);
  return types;
}
export { useBusinessTypes };

const Item = ({ to, label, testid, onClick }) => (
  <NavLink to={to} data-testid={testid} onClick={onClick}
    className={({ isActive }) => `side-link block px-3 py-2 text-[13px] text-slate-300 ${isActive ? "active text-white" : ""}`}>
    {label}
  </NavLink>
);

function canSee(user, key) {
  if (!user) return false;
  if (user.role === "super_admin") return false;
  const perms = user.menu_permissions;
  if (!perms || perms.length === 0) return true;
  return perms.includes(key);
}

const SidebarContent = ({ onNav, user, openGroup, setOpenGroup, pendingCount }) => {
  const isSuperAdmin = user?.role === "super_admin";
  const isAdmin = user?.role === "admin";
  const wsType = user?.workspace_type || "hostnesia";
  const bizTypes = useBusinessTypes();
  const typeLabel = bizTypes.find(t => t.code === wsType)?.label || (wsType === "arang" ? "Bisnis Pabrik Arang" : "Bisnis Online");
  const brandName = isSuperAdmin ? "Super Admin Panel" : typeLabel;
  const brandSub = isSuperAdmin ? "Global" : (user?.workspace_name || brandName);

  const Group = ({ id, icon: Icon, label, children }) => {
    const open = openGroup === id;
    return (
      <div className="mb-1">
        <button
          onClick={() => setOpenGroup(open ? null : id)}
          data-testid={`sidebar-group-${id}`}
          className="side-link w-full flex items-center justify-between px-4 py-2.5 text-sm text-slate-200"
        >
          <span className="flex items-center gap-3"><Icon size={16} className="text-slate-400"/><span>{label}</span></span>
          {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
        </button>
        {open && <div className="ml-8 border-l border-slate-800 pl-3 py-1 space-y-1">{children}</div>}
      </div>
    );
  };

  return (
    <>
      <div className="px-5 py-6 border-b border-slate-800 shrink-0">
        <div className="flex items-center gap-2">
          {isSuperAdmin ? <Shield size={20} style={{color:"#5aaef0"}}/> : <Wallet size={20} style={{color:"#5aaef0"}}/>}
          <div className="min-w-0">
            <div className="font-semibold tracking-tight truncate">Keuangan HN</div>
            <div className="text-[11px] text-slate-400 truncate" data-testid="sidebar-workspace-name">{brandSub}</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-4 min-h-0">
        <div className="px-4 mb-2 text-[11px] uppercase tracking-widest text-slate-500">Menu</div>

        {isSuperAdmin && (
          <>
            <NavLink to="/superadmin/users" data-testid="nav-sa-users" onClick={onNav}
              className={({ isActive }) => `side-link flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-slate-200 ${isActive ? "active text-white" : ""}`}>
              <span className="flex items-center gap-3"><Shield size={16}/> Manajemen User</span>
              {pendingCount > 0 && (
                <span data-testid="sa-pending-badge"
                  className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold animate-pulse">
                  {pendingCount > 99 ? "99+" : pendingCount}
                </span>
              )}
            </NavLink>
            <NavLink to="/superadmin/audit" data-testid="nav-sa-audit" onClick={onNav}
              className={({ isActive }) => `side-link flex items-center gap-3 px-4 py-2.5 text-sm text-slate-200 ${isActive ? "active text-white" : ""}`}>
              <FileText size={16}/> Log Aktivitas (Global)
            </NavLink>
            <NavLink to="/superadmin/security" data-testid="nav-sa-security" onClick={onNav}
              className={({ isActive }) => `side-link flex items-center gap-3 px-4 py-2.5 text-sm text-slate-200 ${isActive ? "active text-white" : ""}`}>
              <Shield size={16}/> Keamanan (Captcha)
            </NavLink>
            <NavLink to="/superadmin/business-types" data-testid="nav-sa-biztypes" onClick={onNav}
              className={({ isActive }) => `side-link flex items-center gap-3 px-4 py-2.5 text-sm text-slate-200 ${isActive ? "active text-white" : ""}`}>
              <Settings size={16}/> Jenis Bisnis
            </NavLink>
            <NavLink to="/pengaturan/profil" data-testid="nav-sa-profile" onClick={onNav}
              className={({ isActive }) => `side-link flex items-center gap-3 px-4 py-2.5 text-sm text-slate-200 ${isActive ? "active text-white" : ""}`}>
              <Settings size={16}/> Profil & Password
            </NavLink>
          </>
        )}

        {!isSuperAdmin && (
          <>
            {canSee(user,"dashboard") && (
              <NavLink to="/dashboard" data-testid="nav-dashboard" onClick={onNav}
                className={({ isActive }) => `side-link flex items-center gap-3 px-4 py-2.5 text-sm text-slate-200 ${isActive ? "active text-white" : ""}`}>
                <LayoutDashboard size={16}/> Dashboard
              </NavLink>
            )}

            {wsType === "arang" && canSee(user,"produksi") && (
              <Group id="produksi" icon={Factory} label="Produksi">
                <Item to="/produksi" label="Data Produksi" testid="nav-produksi" onClick={onNav}/>
              </Group>
            )}

            <Group id="pemasukan" icon={TrendingUp} label="Pemasukan">
              {canSee(user,"pemasukan") && <Item to="/pemasukan" label="Daftar Pemasukan" testid="nav-pemasukan" onClick={onNav}/>}
              {canSee(user,"kategori_pemasukan") && <Item to="/pemasukan/kategori" label="Input Kategori" testid="nav-kategori-pemasukan" onClick={onNav}/>}
              {canSee(user,"pembeli") && <Item to="/pemasukan/pembeli" label="Master Data Pembeli" testid="nav-pembeli" onClick={onNav}/>}
              {wsType === "arang" && <Item to="/piutang" label="Piutang Reminder" testid="nav-piutang" onClick={onNav}/>}
            </Group>
            <Group id="pengeluaran" icon={TrendingDown} label="Pengeluaran">
              {canSee(user,"pengeluaran") && <Item to="/pengeluaran" label="Daftar Pengeluaran" testid="nav-pengeluaran" onClick={onNav}/>}
              {canSee(user,"kategori_pengeluaran") && <Item to="/pengeluaran/kategori" label="Input Kategori" testid="nav-kategori-pengeluaran" onClick={onNav}/>}
            </Group>
            {canSee(user,"hutang_piutang") && (
              <NavLink to="/hutang-piutang" data-testid="nav-hutang-piutang" onClick={onNav}
                className={({ isActive }) => `side-link flex items-center gap-3 px-4 py-2.5 text-sm text-slate-200 ${isActive ? "active text-white" : ""}`}>
                <Wallet size={16}/> Utang & Piutang
              </NavLink>
            )}
            <Group id="laporan" icon={FileText} label="Laporan">
              {canSee(user,"laporan_semua") && <Item to="/laporan/semua" label="Semua Laporan" testid="nav-laporan-semua" onClick={onNav}/>}
              {canSee(user,"laba_rugi") && <Item to="/laporan/laba-rugi" label="Laba Rugi" testid="nav-laba-rugi" onClick={onNav}/>}
              {canSee(user,"neraca") && <Item to="/laporan/neraca" label="Neraca" testid="nav-neraca" onClick={onNav}/>}
              {canSee(user,"arus_kas") && <Item to="/laporan/arus-kas" label="Arus Kas" testid="nav-arus-kas" onClick={onNav}/>}
              {canSee(user,"dividen") && <Item to="/laporan/dividen" label="Pembagian Dividen" testid="nav-dividen" onClick={onNav}/>}
              {canSee(user,"pph_05") && <Item to="/laporan/pph-05" label="Tarif Pajak PPh 0,5%" testid="nav-pph-05" onClick={onNav}/>}
              {canSee(user,"pph_normal") && <Item to="/laporan/pph-normal" label="Tarif Pajak Normal 22%" testid="nav-pph-normal" onClick={onNav}/>}
            </Group>
            <Group id="pengaturan" icon={Settings} label="Pengaturan">
              <Item to="/pengaturan/profil" label="Profil & Password" testid="nav-profile" onClick={onNav}/>
              {isAdmin && canSee(user,"pengaturan") && <Item to="/pengaturan" label="Pengaturan Umum" testid="nav-settings" onClick={onNav}/>}
              {isAdmin && <Item to="/pengaturan/users" label="Manajemen User" testid="nav-users" onClick={onNav}/>}
              {isAdmin && <Item to="/pengaturan/audit" label="Log Aktivitas" testid="nav-audit" onClick={onNav}/>}
              {isAdmin && <Item to="/pengaturan/sampah" label="Kotak Sampah" testid="nav-trash" onClick={onNav}/>}
            </Group>
          </>
        )}
      </nav>
    </>
  );
};

const MobileTab = ({ to, icon: Icon, label, testid, match }) => {
  const location = useLocation();
  const active = match ? match.some(p => location.pathname.startsWith(p)) : location.pathname === to;
  return (
    <NavLink to={to} data-testid={testid}
      className={`mobile-tab ${active?"active":"text-slate-500"} flex flex-col items-center gap-1 py-2 px-1 flex-1 min-w-0`}>
      <span className="mobile-tab-icon h-9 w-14 rounded-full flex items-center justify-center">
        <Icon size={18}/>
      </span>
      <span className="text-[10px] font-medium truncate">{label}</span>
    </NavLink>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const isSuperAdmin = user?.role === "super_admin";
  const pendingCount = usePendingCount(isSuperAdmin);
  return (
    <div className="min-h-screen flex bg-[var(--bg)]">
      {/* Desktop sidebar — fixed height layout so logout stays visible */}
      <aside className="w-64 shrink-0 bg-[var(--sidebar)] text-white flex-col hidden md:flex h-screen sticky top-0">
        <SidebarContent user={user} openGroup={openGroup} setOpenGroup={setOpenGroup} pendingCount={pendingCount}/>
        <div className="border-t border-slate-800 p-4 shrink-0">
          <div className="text-[13px] text-slate-200 truncate">{user?.name || user?.email}</div>
          <div className="text-[11px] text-slate-500 mb-3 truncate">{user?.email} · <span className="brand-text">{user?.role}</span></div>
          <button data-testid="logout-btn" onClick={() => { logout(); nav("/login"); }}
            className="w-full inline-flex items-center gap-2 justify-center bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm px-3 py-2 rounded-sm">
            <LogOut size={14}/> Keluar
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {openDrawer && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="w-72 bg-[var(--sidebar)] text-white flex flex-col h-screen">
            <div className="flex justify-end p-3 shrink-0"><button onClick={()=>setOpenDrawer(false)}><X size={20}/></button></div>
            <SidebarContent onNav={()=>setOpenDrawer(false)} user={user} openGroup={openGroup} setOpenGroup={setOpenGroup} pendingCount={pendingCount}/>
            <div className="border-t border-slate-800 p-4 shrink-0">
              <div className="text-[13px] text-slate-200 truncate">{user?.name}</div>
              <button onClick={()=>{logout();nav("/login");}} className="mt-2 w-full inline-flex items-center gap-2 justify-center bg-slate-800 text-slate-200 text-sm px-3 py-2 rounded-sm">
                <LogOut size={14}/> Keluar
              </button>
            </div>
          </div>
          <div className="flex-1 bg-black/40" onClick={()=>setOpenDrawer(false)}/>
        </div>
      )}

      <main className="flex-1 min-w-0 content-mobile-pad">
        <div className="md:hidden bg-white border-b border-slate-200 flex items-center justify-between px-4 py-3 sticky top-0 z-20">
          <button onClick={()=>setOpenDrawer(true)} data-testid="mobile-menu-btn"><Menu size={22}/></button>
          <div className="flex items-center gap-2">
            <Wallet size={18} style={{color:"#166db4"}}/>
            <span className="font-semibold">Keuangan HN</span>
          </div>
          <button onClick={()=>{logout();nav("/login");}}><LogOut size={18} className="text-slate-500"/></button>
        </div>
        <Outlet/>
      </main>

      {!isSuperAdmin && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-30 flex" data-testid="mobile-bottom-nav">
          <MobileTab to="/dashboard" icon={LayoutDashboard} label="Dashboard" testid="mtab-dashboard"/>
          <MobileTab to="/pemasukan" icon={TrendingUp} label="Pemasukan" testid="mtab-pemasukan" match={["/pemasukan"]}/>
          <MobileTab to="/pengeluaran" icon={TrendingDown} label="Pengeluaran" testid="mtab-pengeluaran" match={["/pengeluaran"]}/>
          <MobileTab to="/laporan/semua" icon={FileText} label="Laporan" testid="mtab-laporan" match={["/laporan"]}/>
        </nav>
      )}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="border-b border-slate-200 bg-white px-4 md:px-8 py-4 md:py-6 flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4 md:sticky md:top-0 md:z-10">
      <div>
        <h1 className="text-xl md:text-2xl font-semibold text-slate-900">{title}</h1>
        {subtitle && <p className="text-xs md:text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2 flex-wrap">{actions}</div>
    </div>
  );
}
